import { useEffect, useRef } from "react";
import { ChevronRight, Lightbulb } from "lucide-react";
import type { Round3Verse } from "@versedrill/core";
import { Button } from "@/components/ui/button";
import { currentLevelIndex, type GameState, type Level } from "@/lib/game.ts";
import { ChoiceList } from "./ChoiceList.tsx";
import { SlotList } from "./SlotList.tsx";

const ROUND = {
  1: { numeral: "I", title: "Find the book", blurb: () => "Work down from the testament to the book these words come from." },
  2: { numeral: "II", title: "Find the section", blurb: (book: string | null) => `Now narrow it down inside ${book ?? "the book"}, one span at a time.` },
  3: { numeral: "III", title: "Find the verse", blurb: () => "Last step: slide the verse into its place among its neighbours." },
} as const;

/** The rounds stacked down the page like leaves turned one after another. */
export function Ladder({
  state,
  hintsAllowed,
  disabled,
  foundText,
  redactionOn,
  onPick,
  onHint,
}: {
  state: GameState;
  hintsAllowed: number;
  disabled: boolean;
  foundText: string;
  redactionOn: boolean;
  onPick: (levelIndex: number, i: number) => void;
  onHint: () => void;
}) {
  const current = currentLevelIndex(state);

  // Bring each newly opened level into view, just under the sticky verse.
  const root = useRef<HTMLDivElement>(null);
  const count = state.levels.length;
  useEffect(() => {
    if (count <= 1) return;
    const last = root.current?.lastElementChild;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    last?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
  }, [count]);

  const trail = (level: Level, idx: number): string[] =>
    state.levels.slice(0, idx).filter((l) => l.round === level.round && l.correct !== null).map((l) => l.choices[l.correct!]!.title);

  return (
    <div ref={root} className="flex flex-col gap-10">
      {state.levels.map((level, idx) => {
        const opensRound = idx === 0 || state.levels[idx - 1]!.round !== level.round;
        const crumbs = trail(level, idx);
        const active = idx === current;
        return (
          <section key={level.id} aria-labelledby={`h-${level.id}`} className="flex scroll-mt-[calc(var(--sticky-h,240px)+16px)] flex-col gap-3">
            {opensRound && (
              <div className="animate-ink-in pt-2">
                <p className="font-ui text-[0.7rem] font-semibold uppercase tracking-[0.22em] text-brand">Round {ROUND[level.round].numeral}</p>
                <h2 id={`h-${level.id}`} className="font-display text-[2.6rem] italic leading-[1.05] sm:text-5xl">
                  {ROUND[level.round].title}
                </h2>
                <p className="mt-1.5 max-w-prose text-[1.02rem] italic text-muted-foreground">{ROUND[level.round].blurb(state.bookName)}</p>
              </div>
            )}
            {crumbs.length > 0 && (
              <nav aria-label="Path so far" className="flex flex-wrap items-center gap-1 font-ui text-xs text-muted-foreground">
                {crumbs.map((c, i) => (
                  <span key={i} className="flex items-center gap-1">
                    {i > 0 && <ChevronRight className="size-3 opacity-60" aria-hidden />}
                    <span className="rounded bg-secondary px-1.5 py-0.5 text-foreground/80">{c}</span>
                  </span>
                ))}
              </nav>
            )}
            {level.round === 3 ? (
              <SlotList level={level} verses={state.slotVerses as Round3Verse[]} redactionOn={redactionOn} foundText={foundText} disabled={disabled} onPick={(i) => onPick(idx, i)} />
            ) : (
              <ChoiceList level={level} disabled={disabled} onPick={(i) => onPick(idx, i)} />
            )}
            {active && state.phase === "playing" && (
              <div className="flex flex-col items-center gap-2 pt-1">
                {hintsAllowed === 0 ? (
                  <p className="font-ui text-xs text-muted-foreground">No hints on an Easy verse.</p>
                ) : level.hinted.length > 0 ? (
                  <p className="text-center font-body italic text-[color-mix(in_oklch,var(--hint)_40%,var(--foreground))]">The answer is one of the highlighted options.</p>
                ) : (
                  <Button variant="outline" onClick={onHint} disabled={disabled || state.hintsLeft <= 0} className="gap-2 border-hint/70 font-ui text-[color-mix(in_oklch,var(--hint)_40%,var(--foreground))] hover:bg-hint/15">
                    <Lightbulb className="size-4" aria-hidden />
                    {state.hintsLeft > 0 ? `Use a hint (${state.hintsLeft} left)` : "No hints left"}
                  </Button>
                )}
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
